import type { ErrorCode } from "@gladis/shared";

/**
 * An error the API returned, with its code intact.
 *
 * Components branch on `code`, never on `message`. A message is copy — it gets reworded, localised,
 * or softened — and a UI that switches on one breaks silently when it does.
 *
 * THAT SENTENCE WAS WRITTEN IN ANTICIPATION AND IS NOW LITERAL. Feature 021 translates every refusal
 * from `code`, in `@gladis/ui/i18n`'s `errorSentence`. `message` remains what it always was: the
 * API-level English default, correct and read by nobody in the browser. Nothing here changed to
 * allow it — this class already carried everything the edge needed.
 *
 * `details` carries the same weight for one family. A date refusal puts its `reason` there —
 * malformed, not-a-date, too-early, too-far — because eight codes across five contexts share those
 * four problems, and a code alone cannot tell them apart.
 */
export class ApiError extends Error {
  constructor(
    public readonly code: ErrorCode | string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /** True when the session is gone — the signal to route to sign-in rather than show an error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  is(code: ErrorCode | string): boolean {
    return this.code === code;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
